import { useEffect, useSyncExternalStore } from 'react';

export type ThemeChoice = 'auto' | 'light' | 'dark';

const KEY = 'capsico:customer:theme';
/** Auto turns dark from 10 PM to 6 AM on the phone's clock. */
const NIGHT_FROM = 22;
const NIGHT_UNTIL = 6;

function stored(): ThemeChoice {
  try {
    const value = window.localStorage.getItem(KEY);
    if (value === 'light' || value === 'dark' || value === 'auto') return value;
  } catch {
    // ignore
  }
  return 'auto';
}

let choice: ThemeChoice = typeof window === 'undefined' ? 'auto' : stored();
const listeners = new Set<() => void>();

export function setThemeChoice(next: ThemeChoice): void {
  choice = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    // ignore
  }
  for (const listener of listeners) listener();
}

export function isNightHour(date: Date): boolean {
  const hour = date.getHours();
  return hour >= NIGHT_FROM || hour < NIGHT_UNTIL;
}

/** Applies the late-night theme to <html> and re-checks every minute in Auto. */
export function useTheme(): {
  choice: ThemeChoice;
  dark: boolean;
  setChoice: typeof setThemeChoice;
} {
  const current = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => choice,
    () => 'auto' as ThemeChoice,
  );
  const tick = useSyncExternalStore(
    (listener) => {
      const timer = window.setInterval(listener, 60_000);
      return () => window.clearInterval(timer);
    },
    () => Math.floor(Date.now() / 60_000),
    () => 0,
  );
  const dark = current === 'dark' || (current === 'auto' && isNightHour(new Date(tick * 60_000)));

  useEffect(() => {
    document.documentElement.classList.toggle('night', dark);
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', dark ? '#18191C' : '#F7B614');
  }, [dark]);

  return { choice: current, dark, setChoice: setThemeChoice };
}
